import * as XLSX from 'xlsx';
import type { SimulationActivity } from '../../../../types/simulation';
import { expandActivitiesForExport } from '../../../../utils/simulation/expandActivitiesForExport';
import {
  inferActivityBoardingAndAlightingCounts,
  inferActivityPassengerDelta,
  inferActivityStopKind,
} from '../../../../utils/simulation/inferActivityStopKind';
import { resolveActivityStopAddress, resolveLegAddress } from './utils/addressResolution';
import { resolveActivityTripId } from './utils';

const getVehicleKey = (a: SimulationActivity): string =>
  (a.vehicle_id ?? a.vehicle_shift_id ?? a.shift_id ?? 'global').toString();

function extractTimeMs(a: SimulationActivity): number {
  const fields = [
    a.activity_start_time,
    a.activity_start,
    a.min_activity_start_time,
    a.start_time,
    (a as Record<string, unknown>).time,
  ];
  for (const field of fields) {
    if (field && typeof field === 'string') {
      const parsed = Date.parse(field);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return Number.MAX_SAFE_INTEGER;
}

function getActivityTypeLabel(a: SimulationActivity, index: number, total: number): 'PICK' | 'DROP' | 'PICK/DROP' | 'START' | 'END' | 'OTHER' {
  const paxDelta = inferActivityPassengerDelta(a);
  if (paxDelta !== null) {
    if (paxDelta > 0) return 'PICK';
    if (paxDelta < 0) return 'DROP';
    const kind = inferActivityStopKind(a);
    if (kind === 'pick' || kind === 'drop') return 'PICK/DROP';
  }
  const kind = inferActivityStopKind(a);
  if (kind === 'pick') return 'PICK';
  if (kind === 'drop') return 'DROP';
  if (kind === 'other') {
    if (index === 0) return 'START';
    if (index === total - 1) return 'END';
    return 'OTHER';
  }
  return 'OTHER';
}

/** Format ISO or date string to HH:mm:ss */
function formatTimeHhMmSs(timeStr: string | undefined): string {
  if (!timeStr || typeof timeStr !== 'string') return '';
  const ms = Date.parse(timeStr);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  const h = d.getUTCHours().toString().padStart(2, '0');
  const m = d.getUTCMinutes().toString().padStart(2, '0');
  const s = d.getUTCSeconds().toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
}

function getStartTime(a: SimulationActivity): string {
  const v =
    a.activity_start_time ??
    a.activity_start ??
    a.min_activity_start_time ??
    a.start_time ??
    (a as Record<string, unknown>).time;
  return v && typeof v === 'string' ? v : '';
}

function getEndTime(a: SimulationActivity): string {
  const v =
    (a as Record<string, unknown>).activity_end_time ??
    (a as Record<string, unknown>).activity_end ??
    (a as Record<string, unknown>).end_time;
  return v && typeof v === 'string' ? v : '';
}

function getLat(a: SimulationActivity): number | '' {
  const n = a.activity_latitude ?? a.latitude ?? (a as Record<string, unknown>).lat;
  return typeof n === 'number' ? n : '';
}

function getLng(a: SimulationActivity): number | '' {
  const n = a.activity_longitude ?? a.longitude ?? (a as Record<string, unknown>).lng;
  return typeof n === 'number' ? n : '';
}

function getActivityCoordinates(
  activity: SimulationActivity
): { lat: number; lng: number } | null {
  const lat = getLat(activity);
  const lng = getLng(activity);
  if (typeof lat === 'number' && typeof lng === 'number') {
    return { lat, lng };
  }
  return null;
}

function haversineMeters(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function isOperationalActivityType(type: string): boolean {
  return type === 'START' || type === 'END' || type === 'OTHER';
}

function isRedundantOperationalDuplicate(
  list: SimulationActivity[],
  index: number
): boolean {
  const currType = getActivityTypeLabel(list[index], index, list.length);
  if (currType !== 'OTHER') return false;

  const currTimeMs = extractTimeMs(list[index]);
  const currCoords = getActivityCoordinates(list[index]);
  if (!Number.isFinite(currTimeMs) || !currCoords) return false;

  const nextIndex = index + 1;
  if (nextIndex < list.length) {
    const nextType = getActivityTypeLabel(list[nextIndex], nextIndex, list.length);
    if (nextType === 'END') {
      const nextTimeMs = extractTimeMs(list[nextIndex]);
      const nextCoords = getActivityCoordinates(list[nextIndex]);
      if (
        Number.isFinite(nextTimeMs) &&
        nextCoords &&
        Math.abs(currTimeMs - nextTimeMs) <= 90_000 &&
        haversineMeters(currCoords.lat, currCoords.lng, nextCoords.lat, nextCoords.lng) <= 150
      ) {
        return true;
      }
    }
  }

  const prevIndex = index - 1;
  if (prevIndex < 0) return false;
  const prevType = getActivityTypeLabel(list[prevIndex], prevIndex, list.length);
  if (!isOperationalActivityType(prevType)) return false;

  const prevTimeMs = extractTimeMs(list[prevIndex]);
  const prevCoords = getActivityCoordinates(list[prevIndex]);
  if (!Number.isFinite(prevTimeMs) || !prevCoords) return false;
  if (Math.abs(currTimeMs - prevTimeMs) > 90_000) return false;

  return haversineMeters(currCoords.lat, currCoords.lng, prevCoords.lat, prevCoords.lng) <= 150;
}

function removeRedundantOperationalDuplicates(
  list: SimulationActivity[]
): SimulationActivity[] {
  return list.filter((_activity, index, arr) => !isRedundantOperationalDuplicate(arr, index));
}

function getRideId(a: SimulationActivity): string {
  return resolveActivityTripId(a as Record<string, unknown>);
}

function getTripId(a: SimulationActivity): string {
  return getRideId(a);
}

function getAddress(a: SimulationActivity): string {
  const addr = resolveActivityStopAddress(a);
  if (addr) return addr;
  const lat = getLat(a);
  const lng = getLng(a);
  if (lat !== '' && lng !== '') return `${lat}, ${lng}`;
  return '';
}

export interface ManifestRow {
  'Activity Num': number;
  Latitude: number | '';
  Longitude: number | '';
  'Arrival Time': string;
  Address: string;
  'Pickup Address': string;
  'Dropoff Address': string;
  'Pax Boarding': number;
  'Pax Alighting': number;
  'Pax On Board': number;
  'Pax Delta': number;
  'Activity Type': string;
  'End Time': string;
  'Trip ID': string;
}

interface ManifestSheetData {
  rows: ManifestRow[];
  firstActivityMs: number;
}

function toNonNegativeCount(value: unknown): number | null {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : NaN;
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return Math.floor(n);
}

function getPassengersArrayCount(a: SimulationActivity): number | null {
  const raw = a as Record<string, unknown>;
  if (!Array.isArray(raw.passengers)) {
    return null;
  }
  if (raw.passengers.length === 0) {
    return 0;
  }
  let total = 0;
  for (const passenger of raw.passengers) {
    if (passenger != null && typeof passenger === 'object') {
      const count = toNonNegativeCount(
        (passenger as Record<string, unknown>).number_of_passengers
      );
      total += count ?? 1;
      continue;
    }
    total += 1;
  }
  return total;
}

function getExplicitPassengerCount(
  a: SimulationActivity,
  kind: 'pick' | 'drop'
): number | null {
  const raw = a as Record<string, unknown>;
  const baseFieldNames =
    kind === 'pick'
      ? [
          'number_of_rides_picked_up',
          'pick_count',
          'pickup_count',
          'pickup_passengers_count',
          'picked_up_passengers_count',
          'passengers_picked_up_count',
          'number_of_passengers_picked_up',
        ]
      : [
          'number_of_rides_dropped_off',
          'drop_count',
          'dropoff_count',
          'drop_off_count',
          'dropoff_passengers_count',
          'passengers_dropped_off_count',
          'number_of_passengers_dropped_off',
        ];

  for (const fieldName of baseFieldNames) {
    const count = toNonNegativeCount(raw[fieldName]);
    if (count !== null) {
      return count;
    }
  }

  const usersField = kind === 'pick' ? raw.users_picked_up : raw.users_dropped_off;
  if (Array.isArray(usersField)) {
    return usersField.length;
  }

  const standardCount = toNonNegativeCount(raw.standard_passengers ?? raw.standard_count);
  const wheelchairCount = toNonNegativeCount(
    raw.wheelchair_passengers ?? raw.wheelchair_count
  );
  if (standardCount !== null || wheelchairCount !== null) {
    return (standardCount ?? 0) + (wheelchairCount ?? 0);
  }

  const totalCount = toNonNegativeCount(raw.passengers_count ?? raw.number_of_passengers);
  if (totalCount !== null) {
    return totalCount;
  }

  return getPassengersArrayCount(a);
}

/** Excel sheet name max 31 chars; disallow : \ / ? * [ ] | */
function sanitizeSheetName(name: string): string {
  return name.replace(/[\\/:*?\[\]|]/g, '_').substring(0, 31);
}

/**
 * Build vehicle groups and transform to manifest rows per vehicle.
 */
function buildManifestSheets(activities: SimulationActivity[]): Map<string, ManifestSheetData> {
  const expanded = expandActivitiesForExport(activities);

  const byVehicle = new Map<string, SimulationActivity[]>();
  expanded.forEach((a) => {
    const key = getVehicleKey(a);
    if (!byVehicle.has(key)) byVehicle.set(key, []);
    byVehicle.get(key)!.push(a);
  });

  const sheets = new Map<string, ManifestSheetData>();

  byVehicle.forEach((list, vehicleId) => {
    const sorted = [...list].sort((a, b) => extractTimeMs(a) - extractTimeMs(b));
    const cleaned = removeRedundantOperationalDuplicates(sorted);
    const firstActivityMs =
      cleaned.length > 0 ? extractTimeMs(cleaned[0]) : Number.MAX_SAFE_INTEGER;
    let paxOnBoard = 0;
    const rows: ManifestRow[] = [];

    cleaned.forEach((a, index) => {
      const activityType = getActivityTypeLabel(a, index, cleaned.length);
      const boardingAlighting = inferActivityBoardingAndAlightingCounts(a);
      const paxBoarding = boardingAlighting?.boarding ?? 0;
      const paxAlighting = boardingAlighting?.alighting ?? 0;
      const inferredDelta = inferActivityPassengerDelta(a);
      let paxDelta = inferredDelta ?? 0;
      if (inferredDelta === null) {
        if (activityType === 'PICK') {
          const count = getExplicitPassengerCount(a, 'pick');
          paxDelta = count ?? 1;
        } else if (activityType === 'DROP') {
          const count = getExplicitPassengerCount(a, 'drop');
          paxDelta = -(count ?? 1);
        }
      }
      paxOnBoard += paxDelta;
      paxOnBoard = Math.max(0, paxOnBoard);

      const lat = getLat(a);
      const lng = getLng(a);
      const pickupAddress =
        resolveLegAddress({ leg: 'pickup', activity: a, fallbackToStopAddress: true, fallbackToCoords: true }) ?? '';
      const dropoffAddress =
        resolveLegAddress({ leg: 'dropoff', activity: a, fallbackToStopAddress: true, fallbackToCoords: true }) ?? '';

      rows.push({
        'Activity Num': index + 1,
        Latitude: lat,
        Longitude: lng,
        'Arrival Time': formatTimeHhMmSs(getStartTime(a)),
        Address: getAddress(a),
        'Pickup Address': pickupAddress,
        'Dropoff Address': dropoffAddress,
        'Pax Boarding': paxBoarding,
        'Pax Alighting': paxAlighting,
        'Pax On Board': paxOnBoard,
        'Pax Delta':
          activityType === 'PICK' || activityType === 'DROP' || activityType === 'PICK/DROP'
            ? paxDelta
            : 0,
        'Activity Type': activityType,
        'End Time': formatTimeHhMmSs(getEndTime(a)),
        'Trip ID': getTripId(a),
      });
    });

    sheets.set(vehicleId, {
      rows,
      firstActivityMs,
    });
  });

  return sheets;
}

/**
 * Download activities as an Excel workbook with one sheet per vehicle.
 * Filename: vehicle_manifest_[runId].xlsx (or vehicle_manifest.xlsx if no runId).
 */
export function downloadManifestXLSX(activities: SimulationActivity[], runId?: string | null): void {
  if (!activities || activities.length === 0) return;

  const workbook = XLSX.utils.book_new();
  const sheets = buildManifestSheets(activities);

  // Sort by first activity start time so Vehicle 1 = earliest (match Gantt order)
  const entries = Array.from(sheets.entries());
  entries.sort(([, sheetA], [, sheetB]) => {
    return sheetA.firstActivityMs - sheetB.firstActivityMs;
  });

  entries.forEach(([, sheet], index) => {
    const sheetName = sanitizeSheetName(`Vehicle ${index + 1}`);
    const worksheet = XLSX.utils.json_to_sheet(sheet.rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  });

  const filename = runId ? `vehicle_manifest_${runId}.xlsx` : 'vehicle_manifest.xlsx';
  XLSX.writeFile(workbook, filename);
}
